
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, ArrowUpRight, Send, Trophy, Bell, Gift, Activity, Loader2, X, AlertTriangle, Crown, CreditCard, Target, Plus, Trash2, Check, LayoutGrid, Lock, PlusCircle, ShoppingBag, Coins } from 'lucide-react';
import { User, Transaction, LeaderboardEntry, Goal, Wallet, Product } from '../types';
import { getTransactions, getAdvancedLeaderboard, transferFunds, redeemVoucher, getGoals, createGoal, deleteGoal, getWallets, createWallet, subscribePerkWallet, deleteWallet, depositToGoal, getProducts } from '../services/storage';
import { Skeleton } from './Skeleton';

interface DashboardProps {
  currentUser: User;
  activeWallet: Wallet | null;
  setActiveWallet: (w: Wallet) => void;
  onBalanceUpdate: (user: User) => void;
  onNavigate: (tab: any) => void;
}

type LeaderboardFilter = 'balance' | 'total_spent' | 'items_bought';

const Dashboard: React.FC<DashboardProps> = ({ currentUser, activeWallet, setActiveWallet, onBalanceUpdate, onNavigate }) => {
  const [greeting, setGreeting] = useState('');
  const [recentTx, setRecentTx] = useState<Transaction[]>([]);
  const [isTxLoading, setIsTxLoading] = useState(true);
  
  // Banking Data
  const [goals, setGoals] = useState<Goal[]>([]);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  
  // Leaderboard State
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState<LeaderboardFilter>('balance');
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);
  
  // Modals
  const [showTransfer, setShowTransfer] = useState(false);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [showManageGoals, setShowManageGoals] = useState(false); 
  const [showWalletsModal, setShowWalletsModal] = useState(false);
  const [showQuickDeposit, setShowQuickDeposit] = useState<{id: string, name: string} | null>(null);

  // Forms
  const [transferTarget, setTransferTarget] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferStatus, setTransferStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [transferMsg, setTransferMsg] = useState('');

  // Create Goal Form
  const [goalType, setGoalType] = useState<'custom' | 'product'>('product');
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalDesc, setNewGoalDesc] = useState(''); 
  const [newGoalTarget, setNewGoalTarget] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [goalStatus, setGoalStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Goal Deposit State
  const [depositGoalAmount, setDepositGoalAmount] = useState('');
  const [goalDepositStatus, setGoalDepositStatus] = useState<'idle' | 'loading'>('idle');

  const [newWalletName, setNewWalletName] = useState('');
  const [walletActionStatus, setWalletActionStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [walletMsg, setWalletMsg] = useState('');

  const [voucherCode, setVoucherCode] = useState('');
  const [redeemStatus, setRedeemStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [redeemMsg, setRedeemMsg] = useState('');

  const safeBalance = activeWallet?.balance ?? 0;

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good Morning');
    else if (hour < 18) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');

    const fetchData = async () => {
      if (recentTx.length === 0) setIsTxLoading(true);
      try {
        const [txs, userGoals, userWallets, products] = await Promise.all([
             getTransactions(currentUser.id),
             getGoals(currentUser.id),
             getWallets(currentUser.id),
             getProducts()
        ]);
        setRecentTx(Array.isArray(txs) ? txs.slice(0, 3) : []);
        setGoals(userGoals);
        setWallets(userWallets);
        setAvailableProducts(products);
      } catch (e) {
        console.warn("Fetch failed", e);
      } finally {
        setIsTxLoading(false);
      }
      fetchLeaderboard('balance'); 
    };
    fetchData();
  }, [currentUser.id, currentUser.balance]); 

  useEffect(() => {
      if (showManageGoals) {
          const refresh = async () => {
              const g = await getGoals(currentUser.id);
              setGoals(g);
          };
          refresh();
      }
  }, [showManageGoals, currentUser.id]);

  const fetchLeaderboard = async (filter: LeaderboardFilter) => {
      if (leaderboardData.length === 0) setIsLeaderboardLoading(true);
      try {
        const data = await getAdvancedLeaderboard(filter);
        setLeaderboardData(Array.isArray(data) ? data : []);
      } catch (e) {
        setLeaderboardData([]);
      } finally {
        setIsLeaderboardLoading(false);
      }
  };

  const handleProductSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const pid = e.target.value;
      setSelectedProductId(pid);
      if (pid) {
          const product = availableProducts.find(p => p.id === pid);
          if (product) {
              setNewGoalName(`Buy ${product.name}`);
              setNewGoalTarget(product.price.toString());
          }
      }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
      e.preventDefault();
      const targetVal = parseFloat(newGoalTarget);
      if (isNaN(targetVal) || targetVal <= 0) {
          alert("Invalid target amount.");
          return;
      }
      setGoalStatus('loading');
      try {
          const res = await createGoal(currentUser.id, newGoalName, targetVal, newGoalDesc, goalType, goalType === 'product' ? selectedProductId : undefined);
          if (res.success) {
              setGoals(await getGoals(currentUser.id));
              setGoalStatus('success');
              setTimeout(() => { 
                  setShowGoalsModal(false); 
                  setGoalStatus('idle'); 
                  setNewGoalName(''); 
                  setNewGoalTarget(''); 
                  setNewGoalDesc('');
                  setSelectedProductId('');
              }, 1500);
          } else {
              setGoalStatus('error');
              alert(`Error: ${res.message}`);
          }
      } catch (err) {
          setGoalStatus('error');
      }
  };

  const handleDeleteGoal = async (id: string) => {
      if (!confirm("Are you sure? Funds will be refunded.")) return;
      const res = await deleteGoal(currentUser.id, id);
      if (res.success) {
          setGoals(await getGoals(currentUser.id));
          onBalanceUpdate({ ...currentUser, balance: currentUser.balance + 0 });
      } else {
          alert(res.message);
      }
  };

  const handleDepositToGoal = async () => {
      if (!showQuickDeposit) return;
      const amount = parseFloat(depositGoalAmount);
      if (isNaN(amount) || amount <= 0) return;
      setGoalDepositStatus('loading');
      const res = await depositToGoal(currentUser.id, showQuickDeposit.id, amount);
      if (res.success) {
          setGoals(await getGoals(currentUser.id));
          onBalanceUpdate({ ...currentUser, balance: currentUser.balance - amount });
          setDepositGoalAmount('');
          setShowQuickDeposit(null);
      } else {
          alert(res.message);
      }
      setGoalDepositStatus('idle');
  };

  const handleCreateCustomWallet = async (e: React.FormEvent) => {
      e.preventDefault();
      setWalletActionStatus('loading');
      const res = await createWallet(currentUser.id, newWalletName);
      if (res) {
          setWallets(await getWallets(currentUser.id));
          setWalletActionStatus('success');
          setNewWalletName('');
      } else {
          setWalletActionStatus('error');
      }
  };

  const handleSubscribePerk = async () => {
      if (!confirm("Subscribe to Perk Wallet for 25 Credits?")) return;
      setWalletActionStatus('loading');
      const res = await subscribePerkWallet(currentUser.id);
      if (res.success) {
          setWallets(await getWallets(currentUser.id));
          setWalletActionStatus('success');
          setWalletMsg(res.message);
          onBalanceUpdate({ ...currentUser, balance: currentUser.balance - 25 });
      } else {
          setWalletActionStatus('error');
          setWalletMsg(res.message);
      }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (transferStatus === 'loading') return;
    const amountVal = parseFloat(transferAmount);
    if (isNaN(amountVal) || amountVal <= 0) return;
    if (amountVal > safeBalance) return; 

    setTransferStatus('loading');
    setTransferMsg('');
    try {
      const result = await transferFunds(currentUser.id, transferTarget, amountVal);
      if (result.success && result.sender) {
        setTransferStatus('success');
        setTransferMsg(`Successfully sent!`);
        onBalanceUpdate(result.sender);
        setTransferTarget('');
        setTransferAmount('');
        setTimeout(() => setShowTransfer(false), 2000);
      } else {
        setTransferStatus('error');
        setTransferMsg(result.message);
      }
    } catch (err) {
      setTransferStatus('error');
      setTransferMsg("System error.");
    }
  };

  const handleRedeem = async () => {
    if (!voucherCode || redeemStatus === 'loading') return;
    setRedeemStatus('loading');
    setRedeemMsg('');
    try {
      const result = await redeemVoucher(currentUser.id, voucherCode.trim().toUpperCase());
      if (result.success && result.user) {
        setRedeemStatus('success');
        setRedeemMsg(result.message);
        onBalanceUpdate(result.user);
        setVoucherCode('');
      } else {
        setRedeemStatus('error');
        setRedeemMsg(result.message);
      }
    } catch (err) {
      setRedeemStatus('error');
      setRedeemMsg('Connection timed out.');
    }
  };

  const txAmount = parseFloat(transferAmount) || 0;
  const canSend = txAmount > 0 && txAmount <= safeBalance;

  return (
    <div className="pb-10 animate-fade-in">
      
      {/* HEADER */}
      <div className="mb-8">
         <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
           {greeting}, <span className="text-accent-500">{currentUser?.username || 'User'}</span>
         </h1>
         <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <p className="text-zinc-500 font-mono text-xs">NETWORK: STABLE</p>
         </div>
      </div>

      {/* WALLET HERO */}
      <div className="relative mb-10 group">
         <div className="absolute inset-0 bg-accent-500/5 blur-[60px] rounded-3xl opacity-50"></div>
         <div className="relative glass-panel rounded-3xl p-8 border border-white/10 overflow-hidden shadow-neon">
            <div className="absolute top-0 right-0 p-8 opacity-20 pointer-events-none">
                 <CreditCard size={120} className="text-accent-500 rotate-12" />
            </div>

            <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-2 bg-black/40 px-3 py-1 rounded-full border border-white/5">
                   <CreditCard size={14} className="text-accent-500" /> 
                   <span className="text-xs font-mono font-bold text-zinc-300 uppercase">{activeWallet?.name || 'Main Wallet'}</span>
                </div>
                {activeWallet?.type === 'perk' && <span className="text-[10px] font-bold bg-purple-500/20 text-purple-400 px-2 py-1 rounded border border-purple-500/30 uppercase tracking-widest">Premium</span>}
            </div>

            <div className="mb-8">
               <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-1">Total Assets</p>
               <div className="flex items-baseline gap-1">
                   <span className="text-3xl md:text-5xl font-bold text-zinc-600 self-start mt-2">$</span>
                   <span className="text-6xl md:text-8xl font-black text-white tracking-tighter drop-shadow-[0_0_15px_rgba(255,140,0,0.5)]">
                     {safeBalance.toFixed(2)}
                   </span>
               </div>
            </div>

            <div className="grid grid-cols-3 gap-3 md:gap-4">
                <button 
                    onClick={() => setShowTransfer(true)}
                    className="glass-panel hover:bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 transition-all active:scale-95 group"
                >
                    <Send size={20} className="text-white group-hover:text-accent-500 transition-colors" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Transfer</span>
                </button>
                <button 
                    onClick={() => setShowManageGoals(true)}
                    className="glass-panel hover:bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 transition-all active:scale-95 group"
                >
                    <Target size={20} className="text-white group-hover:text-blue-500 transition-colors" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Goals</span>
                </button>
                <button 
                    onClick={() => setShowWalletsModal(true)}
                    className="glass-panel hover:bg-white/5 border border-white/10 p-4 rounded-2xl flex flex-col items-center justify-center gap-2 transition-all active:scale-95 group"
                >
                    <LayoutGrid size={20} className="text-white group-hover:text-purple-500 transition-colors" />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Wallets</span>
                </button>
            </div>
         </div>
      </div>

      {/* DASHBOARD GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
         
         {/* Recent Activity */}
         <div className="glass-panel rounded-3xl p-6 border border-white/5 flex flex-col h-full min-h-[300px]">
             <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <Activity size={18} className="text-zinc-500" /> Activity
                </h3>
                <button onClick={() => onNavigate('history')} className="text-xs text-accent-500 hover:text-white transition-colors uppercase font-bold">View All</button>
             </div>
             
             <div className="space-y-4 flex-1">
                 {isTxLoading ? (
                     <div className="space-y-4">
                         <Skeleton className="h-12 w-full rounded-xl" />
                         <Skeleton className="h-12 w-full rounded-xl" />
                     </div>
                 ) : recentTx.length > 0 ? (
                     recentTx.map(tx => (
                         <div key={tx.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                             <div className="flex items-center gap-3 overflow-hidden">
                                 <div className={`w-10 h-10 rounded-full flex items-center justify-center border border-white/5 shrink-0 ${['purchase','deduction','transfer_out'].includes(tx.type) ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
                                     {['purchase','deduction','transfer_out'].includes(tx.type) ? <ArrowUpRight size={16}/> : <ArrowUpRight size={16} className="rotate-180"/>}
                                 </div>
                                 <div className="truncate">
                                     <p className="text-sm font-bold text-white truncate">{tx.description}</p>
                                     <p className="text-[10px] text-zinc-500 font-mono">{new Date(tx.created_at).toLocaleDateString()}</p>
                                 </div>
                             </div>
                             <span className={`font-mono font-bold text-sm whitespace-nowrap ${['purchase','deduction','transfer_out'].includes(tx.type) ? 'text-zinc-400' : 'text-green-500'}`}>
                                 {['purchase','deduction','transfer_out'].includes(tx.type) ? '-' : '+'}${tx.amount.toFixed(2)}
                             </span>
                         </div>
                     ))
                 ) : (
                     <div className="flex flex-col items-center justify-center h-full text-zinc-600 gap-2">
                         <Activity size={32} className="opacity-20" />
                         <p className="text-xs font-mono">NO DATA LOGGED</p>
                     </div>
                 )}
             </div>
         </div>

         {/* Leaderboard */}
         <div className="glass-panel rounded-3xl p-6 border border-white/5 flex flex-col h-full min-h-[300px]">
             <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-500" /> Elite Rank
                </h3>
             </div>

             <div className="flex bg-black/40 p-1 rounded-xl mb-4 border border-white/5">
                {(['balance', 'total_spent'] as const).map(f => (
                    <button
                        key={f}
                        onClick={() => { setLeaderboardFilter(f); fetchLeaderboard(f); }}
                        className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-lg transition-all ${leaderboardFilter === f ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                    >
                        {f === 'balance' ? 'Net Worth' : 'Spender'}
                    </button>
                ))}
             </div>

             <div className="space-y-3 flex-1">
                 {isLeaderboardLoading ? (
                     <Skeleton className="h-full w-full rounded-xl" />
                 ) : (
                     leaderboardData.slice(0, 3).map((entry, i) => (
                         <div key={i} className={`flex items-center gap-4 p-3 rounded-xl border ${i===0 ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-white/5 bg-white/5'}`}>
                             <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${i===0 ? 'bg-yellow-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                                 {i + 1}
                             </div>
                             <div className="flex-1 truncate">
                                 <p className="font-bold text-sm text-white truncate">{entry.username}</p>
                                 {currentUser.id === entry.id && <span className="text-[10px] text-accent-500 uppercase font-bold">You</span>}
                             </div>
                             <div className="text-right">
                                 {i===0 && <Crown size={12} className="text-yellow-500 ml-auto mb-1" />}
                                 <p className="font-mono text-sm font-bold text-zinc-300">${entry.balance.toFixed(2)}</p>
                             </div>
                         </div>
                     ))
                 )}
             </div>
         </div>

         {/* Voucher Redeem */}
         <div className="lg:col-span-2 glass-panel rounded-3xl p-6 border border-white/5">
             <div className="flex flex-col md:flex-row gap-4 items-center">
                 <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center border border-green-500/20 shrink-0">
                     <Gift size={24} className="text-green-500" />
                 </div>
                 <div className="flex-1 w-full">
                     <h3 className="font-bold text-white">Redeem Voucher</h3>
                     <p className="text-zinc-500 text-xs">Enter your code to claim credits.</p>
                 </div>
                 <div className="flex gap-2 w-full md:w-auto">
                     <input 
                        type="text" 
                        placeholder="CODE" 
                        className="flex-1 bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-green-500 outline-none font-mono uppercase w-full"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                     />
                     <button 
                        onClick={handleRedeem}
                        disabled={redeemStatus === 'loading'}
                        className="bg-green-600 hover:bg-green-500 text-white px-6 rounded-xl font-bold text-xs disabled:opacity-50"
                     >
                        {redeemStatus === 'loading' ? <Loader2 className="animate-spin" /> : 'CLAIM'}
                     </button>
                 </div>
             </div>
             {redeemMsg && (
                 <p className={`text-xs mt-3 text-center font-mono font-bold ${redeemStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                     {redeemMsg}
                 </p>
             )}
         </div>

      </div>

      {/* --- MODALS --- */}

      {/* TRANSFER MODAL - BANK STYLE */}
      <AnimatePresence>
      {showTransfer && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
          <motion.div initial={{scale: 0.95, opacity: 0}} animate={{scale: 1, opacity: 1}} exit={{scale: 0.95, opacity: 0}} className="glass-panel w-full max-w-sm rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
             <div className="p-6 border-b border-white/5 flex justify-between items-center bg-black/40">
               <h3 className="font-bold text-white uppercase tracking-widest text-sm">Send Credits</h3>
               <button onClick={() => setShowTransfer(false)}><X size={20} className="text-zinc-500 hover:text-white"/></button>
             </div>
             
             <div className="p-6 space-y-6">
                 <div>
                    <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-2 block">Recipient Username</label>
                    <input 
                        className="w-full bg-black/40 border border-white/10 p-4 text-white rounded-xl focus:border-accent-500 outline-none text-sm font-bold" 
                        placeholder="Search user..." 
                        value={transferTarget} 
                        onChange={e => setTransferTarget(e.target.value)} 
                    />
                 </div>
                 
                 <div>
                    <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-2 block">Amount</label>
                    <div className="relative">
                        <span className="absolute left-4 top-4 text-zinc-500 text-lg">$</span>
                        <input 
                            className="w-full bg-black/40 border border-white/10 p-4 pl-8 text-white rounded-xl font-mono text-xl font-bold focus:border-accent-500 outline-none" 
                            type="number" 
                            placeholder="0.00" 
                            value={transferAmount} 
                            onChange={e => setTransferAmount(e.target.value)} 
                        />
                    </div>
                 </div>

                 <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5 space-y-2">
                     <div className="flex justify-between text-xs">
                         <span className="text-zinc-500">Service Fee</span>
                         <span className="text-white font-mono">$0.00</span>
                     </div>
                     <div className="flex justify-between text-xs pt-2 border-t border-white/5">
                         <span className="text-zinc-400 font-bold">Total Debit</span>
                         <span className="text-accent-500 font-mono font-bold">${txAmount.toFixed(2)}</span>
                     </div>
                 </div>

                 {transferMsg && <p className={`text-xs text-center font-bold ${transferStatus === 'error' ? 'text-red-500' : 'text-green-500'}`}>{transferMsg}</p>}
                 
                 <button 
                    onClick={handleTransfer}
                    disabled={!canSend || transferStatus === 'loading'}
                    className="w-full bg-accent-500 hover:bg-accent-400 text-black font-bold py-4 rounded-xl uppercase tracking-wider text-xs shadow-neon transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none"
                 >
                    {transferStatus === 'loading' ? <Loader2 className="animate-spin mx-auto" /> : 'Confirm Transaction'}
                 </button>
             </div>
          </motion.div>
        </div>
      )}
      </AnimatePresence>

      {/* MANAGE GOALS HUB - LUXURY REDESIGN */}
      <AnimatePresence>
      {showManageGoals && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
           <motion.div 
             initial={{ opacity: 0, scale: 0.95 }}
             animate={{ opacity: 1, scale: 1 }}
             exit={{ opacity: 0, scale: 0.95 }}
             className="glass-panel w-full max-w-2xl rounded-3xl border border-white/10 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
           >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-black/40 shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-500/30">
                        <Target className="text-blue-500" size={20} />
                    </div>
                    <div>
                        <h3 className="font-bold text-white text-lg">Goal Manager</h3>
                        <p className="text-zinc-500 text-xs font-mono">TRACKING {goals.length} ACTIVE</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                     <button onClick={() => setShowGoalsModal(true)} className="bg-zinc-800 hover:bg-zinc-700 text-white p-2 rounded-lg border border-white/10">
                         <Plus size={20} />
                     </button>
                     <button onClick={() => setShowManageGoals(false)} className="text-zinc-500 hover:text-white p-2"><X size={20}/></button>
                  </div>
              </div>

              <div className="p-6 overflow-y-auto space-y-4">
                  {goals.length === 0 ? (
                      <div className="text-center py-20 bg-white/5 rounded-2xl border border-dashed border-white/10">
                          <Target size={48} className="mx-auto text-zinc-700 mb-4" />
                          <p className="text-zinc-500 text-sm font-bold">No Goals Active</p>
                          <button onClick={() => setShowGoalsModal(true)} className="mt-4 text-accent-500 text-xs uppercase font-bold hover:underline">Create One Now</button>
                      </div>
                  ) : (
                      goals.map(g => {
                          const now = new Date();
                          const created = new Date(g.created_at);
                          const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
                          const isLocked = hoursDiff < 48;
                          const percentage = g.target_amount > 0 ? (g.current_amount / g.target_amount) * 100 : 0;
                          const safePercentage = Math.min(100, Math.max(0, percentage));

                          return (
                              <div key={g.id} className="bg-zinc-900/50 border border-white/5 rounded-2xl p-4 flex gap-4 items-center group hover:border-white/10 transition-all">
                                  {/* Product Image Thumbnail */}
                                  <div className="w-16 h-16 rounded-xl bg-black border border-white/10 overflow-hidden shrink-0 relative">
                                      {g.product?.image ? (
                                          <img src={g.product.image} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" alt="" />
                                      ) : (
                                          <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-600"><Target size={20} /></div>
                                      )}
                                      {isLocked && (
                                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[1px]">
                                              <Lock size={16} className="text-zinc-400" />
                                          </div>
                                      )}
                                  </div>

                                  <div className="flex-1 min-w-0">
                                      <div className="flex justify-between items-start mb-1">
                                          <div>
                                             <h4 className="font-bold text-white truncate text-sm">{g.name}</h4>
                                             <p className="text-[10px] text-zinc-500 truncate">{g.description || 'No description'}</p>
                                          </div>
                                          <div className="text-right">
                                              <span className="block text-white font-mono font-bold text-sm">${g.current_amount.toFixed(2)}</span>
                                              <span className="text-[10px] text-zinc-500">of ${g.target_amount.toFixed(2)}</span>
                                          </div>
                                      </div>
                                      
                                      <div className="w-full bg-black h-1.5 rounded-full overflow-hidden mb-3">
                                          <div className="bg-orange-500 h-full rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)]" style={{ width: `${safePercentage}%` }}></div>
                                      </div>

                                      <div className="flex gap-2">
                                          <button 
                                              onClick={() => setShowQuickDeposit({ id: g.id, name: g.name })}
                                              className="bg-blue-600/10 hover:bg-blue-600 text-blue-500 hover:text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase border border-blue-500/30 transition-colors flex-1"
                                          >
                                              Deposit Funds
                                          </button>
                                          {isLocked ? (
                                              <div className="px-3 py-1.5 rounded-lg border border-white/5 bg-black/20 text-zinc-600 text-[10px] font-bold uppercase cursor-not-allowed flex items-center gap-1">
                                                 <Lock size={10} /> {Math.ceil(48 - hoursDiff)}h Lock
                                              </div>
                                          ) : (
                                              <button 
                                                  onClick={() => handleDeleteGoal(g.id)}
                                                  className="bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase border border-red-500/30 transition-colors"
                                              >
                                                  Refund
                                              </button>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          );
                      })
                  )}
              </div>
           </motion.div>
        </div>
      )}
      </AnimatePresence>

      {/* CREATE GOAL MODAL */}
      <AnimatePresence>
      {showGoalsModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="glass-panel w-full max-w-sm rounded-3xl border border-white/10 shadow-2xl p-6"
          >
             <div className="flex justify-between items-center mb-6">
                 <h3 className="font-bold text-white uppercase tracking-wider text-sm">Create New Goal</h3>
                 <button onClick={() => setShowGoalsModal(false)}><X size={20} className="text-zinc-500 hover:text-white"/></button>
             </div>

             <div className="flex bg-zinc-900 p-1 rounded-xl mb-6 border border-white/5">
                <button 
                   onClick={() => setGoalType('product')}
                   className={`flex-1 py-2 text-xs font-bold uppercase rounded-lg transition-all ${goalType === 'product' ? 'bg-orange-500 text-black shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                   Product
                </button>
                <button 
                   onClick={() => setGoalType('custom')}
                   className={`flex-1 py-2 text-xs font-bold uppercase rounded-lg transition-all ${goalType === 'custom' ? 'bg-orange-500 text-black shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
                >
                   Custom
                </button>
             </div>

             <form onSubmit={handleCreateGoal} className="space-y-4">
                 {goalType === 'product' ? (
                     <div>
                        <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-2 block">Market Item</label>
                        <select 
                            className="w-full bg-black/40 border border-white/10 p-4 text-white rounded-xl focus:border-accent-500 outline-none appearance-none text-sm"
                            value={selectedProductId}
                            onChange={handleProductSelect}
                            required
                        >
                            <option value="" disabled>Select from Market...</option>
                            {availableProducts.map(p => (
                                <option key={p.id} value={p.id}>{p.name} - ${p.price.toFixed(2)}</option>
                            ))}
                        </select>
                     </div>
                 ) : (
                     <div>
                        <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-2 block">Name</label>
                        <input 
                            className="w-full bg-black/40 border border-white/10 p-4 text-white rounded-xl focus:border-accent-500 outline-none text-sm"
                            placeholder="e.g. New PC" 
                            value={newGoalName} 
                            onChange={e => setNewGoalName(e.target.value)} 
                            required 
                        />
                     </div>
                 )}

                 <div>
                     <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-wider mb-2 block">Target ($)</label>
                     <input 
                        className="w-full bg-black/40 border border-white/10 p-4 text-white rounded-xl font-mono text-sm font-bold focus:border-accent-500 outline-none disabled:opacity-50"
                        type="number" 
                        placeholder="0.00" 
                        value={newGoalTarget} 
                        onChange={e => setNewGoalTarget(e.target.value)} 
                        required 
                        disabled={goalType === 'product'} 
                     />
                 </div>

                 <button 
                    disabled={goalStatus === 'loading'}
                    className="w-full bg-accent-500 hover:bg-accent-400 text-black font-bold py-4 rounded-xl uppercase tracking-wider text-xs shadow-neon transition-all active:scale-95 mt-4"
                 >
                    {goalStatus === 'loading' ? <Loader2 className="animate-spin mx-auto" /> : 'Create Goal'}
                 </button>
             </form>
          </motion.div>
        </div>
      )}
      </AnimatePresence>

      {/* QUICK DEPOSIT POPUP */}
      <AnimatePresence>
          {showQuickDeposit && (
              <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                  <motion.div 
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="glass-panel w-full max-w-xs rounded-3xl border border-white/10 shadow-2xl p-6"
                  >
                      <h4 className="text-white font-bold mb-1">Quick Deposit</h4>
                      <p className="text-zinc-500 text-xs mb-4">Adding funds to {showQuickDeposit.name}</p>
                      
                      <input 
                            className="w-full bg-black/40 border border-white/10 text-white p-3 rounded-xl text-lg font-mono font-bold focus:border-blue-500 outline-none mb-4 text-center"
                            placeholder="$0.00"
                            type="number"
                            autoFocus
                            value={depositGoalAmount}
                            onChange={e => setDepositGoalAmount(e.target.value)}
                      />
                      
                      <div className="grid grid-cols-2 gap-3">
                          <button 
                              onClick={() => { setShowQuickDeposit(null); setDepositGoalAmount(''); }}
                              className="py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-xl font-bold text-xs uppercase"
                          >
                              Cancel
                          </button>
                          <button 
                              onClick={handleDepositToGoal}
                              className="py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs uppercase"
                          >
                              {goalDepositStatus === 'loading' ? '...' : 'Confirm'}
                          </button>
                      </div>
                  </motion.div>
              </div>
          )}
      </AnimatePresence>

      {/* WALLETS MODAL (Placeholder reuse logic) */}
      {showWalletsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl">
           <div className="glass-panel w-full max-w-md rounded-3xl border border-white/10 p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-white uppercase tracking-wider text-sm">Select Wallet</h3>
                    <button onClick={() => setShowWalletsModal(false)}><X size={20} className="text-zinc-500 hover:text-white"/></button>
                </div>
                <div className="space-y-3">
                    {wallets.map(w => (
                         <div 
                            key={w.id} 
                            onClick={() => { setActiveWallet(w); localStorage.setItem('active_wallet_id', w.id); setShowWalletsModal(false); }}
                            className={`p-4 rounded-xl border cursor-pointer flex justify-between items-center transition-all ${activeWallet?.id === w.id ? 'bg-accent-500/10 border-accent-500' : 'bg-zinc-900/50 border-white/5 hover:bg-white/5'}`}
                         >
                             <div>
                                 <p className="font-bold text-white text-sm">{w.name} {w.type === 'perk' && '💎'}</p>
                                 <p className="text-xs text-zinc-500 font-mono">${w.balance.toFixed(2)}</p>
                             </div>
                             {activeWallet?.id === w.id && <Check size={16} className="text-accent-500"/>}
                         </div>
                    ))}
                </div>
                {/* Simplified create/sub view for brevity in polish pass */}
                <div className="mt-6 pt-4 border-t border-white/5">
                     <button onClick={handleSubscribePerk} className="w-full py-3 border border-purple-500/30 text-purple-400 rounded-xl text-xs font-bold uppercase hover:bg-purple-500/10">
                        Get Perk Wallet (+25 Credits)
                     </button>
                </div>
           </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
